import React, { useState } from 'react';
import Icon from './AppIcon';

const DocumentsSection = ({ documents = [], onUpdateDocuments, isReadOnly = false }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [loadingDocId, setLoadingDocId] = useState(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setIsUploading(true);

    try {
      for (const file of files) {
        // Create a simple document object with file info
        const newDoc = {
          id: `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString(),
          // In a real implementation, you'd upload to storage service (S3, Supabase Storage, etc.)
          url: URL.createObjectURL(file) // For demo - in production, use real file storage
        };

        onUpdateDocuments([...documents, newDoc]);
      }
    } catch (error) {
      console.error('Error handling file upload:', error);
    } finally {
      setIsUploading(false);
      // Reset the input
      e.target.value = '';
    }
  };

  const handleRemoveDocument = (docId) => {
    if (!confirm('Remove this document?')) return;
    onUpdateDocuments(documents.filter(d => d.id !== docId));
  };

  const handleViewDocument = async (doc) => {
    try {
      setLoadingDocId(doc.id);

      // Check if url is available
      if (!doc.url) {
        console.error('Document URL not found:', doc);
        alert('Error: Document URL not available');
        return;
      }

      // Convert base64 data URL to blob and open in new tab
      if (doc.url.startsWith('data:')) {
        try {
          // Extract base64 from data URL: "data:application/pdf;base64,JVBERi0xLjQK..."
          const base64 = doc.url.split(',')[1];

          if (!base64) {
            throw new Error('Invalid data URL format');
          }

          // Decode base64 to bytes
          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);

          // Create blob from byte array
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);

          // Open in new tab
          window.open(blobUrl, '_blank');

          // Clean up blob URL after a short delay
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

          console.log('✅ Document opened successfully in new tab');
        } catch (conversionError) {
          console.error('Error converting base64 to blob:', conversionError);
          alert('Error: Could not open document. Please try downloading instead.');
        }
      } else if (doc.url.startsWith('blob:')) {
        // Already a blob URL, open directly
        window.open(doc.url, '_blank');
        setTimeout(() => URL.revokeObjectURL(doc.url), 1000);
      } else {
        // For other URLs, try to open directly
        window.open(doc.url, '_blank');
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      alert('Error: Could not open document. Please try downloading instead.');
    } finally {
      setLoadingDocId(null);
    }
  };

  const handleDownloadDocument = (doc) => {
    try {
      if (!doc.url) {
        console.error('Document URL not found:', doc);
        alert('Error: Document URL not available');
        return;
      }

      // Create a temporary download link
      const link = document.createElement('a');
      link.href = doc.url;
      link.download = doc.name || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URLs
      if (doc.url.startsWith('blob:')) {
        setTimeout(() => URL.revokeObjectURL(doc.url), 1000);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error: Could not download document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'FileText';
      case 'doc':
      case 'docx':
        return 'FileText';
      case 'xls':
      case 'xlsx':
        return 'FileText';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'Image';
      default:
        return 'File';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-black flex items-center space-x-2">
            <Icon name="File" size={20} />
            <span>Documents</span>
          </h2>
          {!isReadOnly && (
            <label className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors cursor-pointer text-sm font-medium inline-block">
              + Add Document
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                disabled={isUploading}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
              />
            </label>
          )}
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {documents.map((doc) => (
          <div key={doc.id} className="p-6 hover:bg-gray-50 transition-colors">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                <Icon name={getFileIcon(doc.name)} size={20} color="#666" />
              </div>
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => handleDownloadDocument(doc)}
                  disabled={loadingDocId === doc.id}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline truncate block transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                  title="Click to download document"
                >
                  {loadingDocId === doc.id && loadingDocId === doc.id ? (
                    <div className="flex items-center space-x-1">
                      <Icon name="Loader" size={14} className="animate-spin" />
                      <span>Opening...</span>
                    </div>
                  ) : (
                    doc.name
                  )}
                </button>
                <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                  <span>{formatFileSize(doc.size)}</span>
                  {doc.uploadedAt && (
                    <>
                      <span>•</span>
                      <span>
                        {new Date(doc.uploadedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  onClick={() => handleViewDocument(doc)}
                  disabled={loadingDocId === doc.id}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="View in browser"
                >
                  <Icon name="Eye" size={18} />
                </button>
                <button
                  onClick={() => handleDownloadDocument(doc)}
                  className="p-2 text-gray-400 hover:text-black hover:bg-gray-200 rounded transition-colors"
                  title="Download"
                >
                  <Icon name="Download" size={18} />
                </button>
                {!isReadOnly && (
                  <button
                    onClick={() => handleRemoveDocument(doc.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Remove"
                  >
                    <Icon name="X" size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {documents.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="File" size={40} className="mx-auto mb-3 opacity-20" />
          <p className="text-gray-500 text-sm">
            {isReadOnly ? 'No documents available.' : 'No documents added yet. Click "Add Document" to upload files.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default DocumentsSection;
